import { FC } from 'react';
import { LabelBold, Small } from '../typography/typography';
import InputVariantPill from './input-variant-pill';

interface Props {
	children?: React.ReactNode;
	title: string;
	label?: string;
	id: string;
	isChecked: boolean;
	disabled?: boolean;
	onChange: React.ChangeEventHandler<HTMLInputElement>;
	className?: string;
	labelClassName?: string;
	inputClassName?: string;
}

const RadioButton: FC<Props> = ({
	children,
	title,
	label,
	isChecked,
	id,
	onChange,
	disabled,
	className,
	labelClassName,
	inputClassName
}) => (
	<fieldset className={className ? `custom custom--radio ${className}` : 'custom custom--radio'}>
		<input
			type="radio"
			className={inputClassName ? `custom__input ${inputClassName}` : 'custom__input'}
			id={id}
			name={id}
			value={id}
			checked={isChecked}
			onChange={onChange}
			disabled={disabled}
			data-at={`radio-button-${id}`}
		/>
		<label
			htmlFor={id}
			className={
				labelClassName
					? `custom__label custom-right-radio ${labelClassName}`
					: 'custom__label custom-right-radio'
			}
		>
			<LabelBold>{title}</LabelBold>
			{children}
			{label && <Small>{label}</Small>}
		</label>
	</fieldset>
);

interface RadioButtonPillProps extends React.InputHTMLAttributes<HTMLInputElement> {
	isChecked: boolean;
	displayLabel?: string;
	className?: string;
	children?: React.ReactNode;
}

/**
 * Radio button input which looks like a pill.
 *
 * @param {string} id - Identifier of input.
 * @param {boolean} isChecked - Value that tells whether this input is visually checked.
 * @param {string} displayLabel - Label of input that is shown to the user.
 * @param {JSX.element} children - Custom children component to show instead of label.
 */
export const RadioButtonPill: React.FC<RadioButtonPillProps> = ({
	id,
	displayLabel,
	children,
	className,
	isChecked,
	...props
}) => {
	return (
		<InputVariantPill
			id={id}
			type="radio"
			displayLabel={displayLabel}
			className={className}
			isActive={isChecked}
			{...props}
		>
			{children}
		</InputVariantPill>
	);
};

export default RadioButton;
